import React, { useEffect, useState } from "react";
import { Trophy, Newspaper, ArrowUpRight, CircleNotch } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { listAwards } from "@/lib/api";

const MAGAZINE_COVER = "https://customer-assets.emergentagent.com/job_gifted-carson-6/artifacts/9f4ty1pd_Speciale%20Awards%20del%20Serramento%202025.jpg";

const RIVISTE = [
  {
    title: "Nuova Finestra",
    subtitle: "Speciale Awards del Serramento 2025",
    image: MAGAZINE_COVER,
    url: "https://www.guidafinestra.it",
    type: "cover",
  },
  {
    title: "Serramenti Design e Componenti",
    subtitle: "Novembre 2023 · Korus Laccatura ad acqua",
    image: "https://customer-assets.emergentagent.com/job_gifted-carson-6/artifacts/v74m4z49_SERRAMENTI_DESIGN_E_COMPONENTI_N_9_novembre_2023_page-0001.jpg",
    url: "https://www.serramentinews.it",
    type: "cover",
  },
  {
    title: "Showroom Porte & Finestre",
    subtitle: "Aprile-Maggio 2025 · Korus Win Box",
    image: "https://customer-assets.emergentagent.com/job_gifted-carson-6/artifacts/dly5pdc2_showroom%20porte%20finestre_page-0001.jpg",
    url: "https://customer-assets.emergentagent.com/job_gifted-carson-6/artifacts/ow48lc9v_showroom%20porte%20finestre.pdf",
    type: "cover",
  },
];

export default function PremiPage() {
  const [awards, setAwards] = useState([]);
  const [loadingAwards, setLoadingAwards] = useState(true);
  const [awardsError, setAwardsError] = useState("");

  useEffect(() => {
    listAwards()
      .then((r) => setAwards(r.items || []))
      .catch((e) => setAwardsError(e.message))
      .finally(() => setLoadingAwards(false));
  }, []);

  const statPremi = awards.length;
  const statBrand = new Set(awards.map((a) => a.brand).filter(Boolean)).size;
  const statAnni  = new Set(awards.map((a) => a.year).filter(Boolean)).size;

  return (
    <div data-testid="premi-page" className="pt-[140px] pb-24">
      <div className="max-w-[1480px] mx-auto px-5 sm:px-8 lg:px-12">
        <div className="overline mb-6">Riconoscimenti · Awards del Serramento</div>
        <h1 className="font-display text-[clamp(2.8rem,7vw,6.5rem)] leading-[0.9] tracking-tighter font-medium max-w-5xl">
          Vincitori <em className="italic font-light text-[#EB0D0D]">2024 · 2025</em>.
        </h1>
        <p className="mt-8 text-lg md:text-xl text-[#5C5A55] max-w-2xl leading-relaxed">
          I marchi che proponiamo hanno ottenuto importanti riconoscimenti agli <strong>Awards del Serramento</strong>, l'evento di riferimento del settore organizzato da <strong>Guida Finestra</strong>.
        </p>

        {/* Magazine cover hero */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mt-20 items-center">
          <div className="lg:col-span-5">
            <div className="bg-[#F0EFEB] p-5 inline-block">
              <img
                src={MAGAZINE_COVER}
                alt="Nuova Finestra — Speciale Awards del Serramento 2025"
                className="w-full max-w-md h-auto object-contain shadow-xl"
              />
            </div>
          </div>
          <div className="lg:col-span-7">
            <Newspaper size={40} weight="duotone" className="text-[#EB0D0D] mb-5" />
            <div className="overline mb-3">Pubblicazione ufficiale</div>
            <h2 className="font-display text-3xl md:text-5xl tracking-tighter leading-[0.95] mb-5">
              Nuova Finestra<br />
              <span className="text-[#5C5A55] italic font-light">Speciale Awards del Serramento 2025</span>
            </h2>
            <p className="text-[#5C5A55] leading-relaxed text-lg">
              I prodotti che installiamo sono protagonisti delle pagine di <strong>Nuova Finestra</strong>, la rivista di riferimento del settore. Una garanzia di qualità riconosciuta a livello nazionale.
            </p>
            <a
              href="https://www.guidafinestra.it"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="cta-guidafinestra"
              className="inline-flex items-center gap-2 mt-8 text-sm uppercase tracking-[0.15em] font-bold text-[#1A1918] hover:text-[#EB0D0D] transition-colors border-b border-[#1A1918] pb-1"
            >
              guidafinestra.it <ArrowUpRight size={14} weight="bold" />
            </a>
          </div>
        </div>

        {/* Riviste / pubblicazioni */}
        <div className="mt-20">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
            <div>
              <Newspaper size={40} weight="duotone" className="text-[#EB0D0D] mb-4" />
              <div className="overline mb-3">Riviste & pubblicazioni</div>
              <h2 className="font-display text-4xl md:text-5xl tracking-tighter leading-[0.95]">
                Sui nostri prodotti<br />
                <em className="italic font-light text-[#EB0D0D]">hanno scritto</em>.
              </h2>
            </div>
            <p className="max-w-md text-[#5C5A55] leading-relaxed">
              Le riviste di settore raccontano regolarmente i prodotti che proponiamo. Una garanzia di qualità riconosciuta a livello nazionale.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {RIVISTE.map((r, idx) => {
              const isClickable = !!r.url;
              const Wrapper = isClickable ? "a" : "div";
              const wrapperProps = isClickable
                ? { href: r.url, target: "_blank", rel: "noopener noreferrer", "data-testid": `rivista-${idx}` }
                : { "data-testid": `rivista-${idx}` };
              return (
                <Wrapper
                  key={idx}
                  {...wrapperProps}
                  className={`group bg-[#F0EFEB] border border-[#D1CFC7]/40 overflow-hidden flex flex-col ${isClickable ? "hover:border-[#EB0D0D] transition-colors" : ""}`}
                >
                  {r.image ? (
                    <div className="relative aspect-[3/4] overflow-hidden bg-[#1A1918] flex items-center justify-center">
                      <img
                        src={r.image}
                        alt={`${r.title} — ${r.subtitle}`}
                        className="w-full h-full object-contain transition-transform duration-700 group-hover:scale-[1.03]"
                      />
                    </div>
                  ) : (
                    <div className="relative aspect-[3/4] overflow-hidden bg-[#1A1918] text-[#FAF9F6] flex flex-col items-center justify-center p-8">
                      <Newspaper size={80} weight="duotone" className="text-[#EB0D0D] mb-6" />
                      <div className="text-xs uppercase tracking-[0.25em] text-[#EB0D0D] mb-3">PDF</div>
                      <div className="font-display text-2xl text-center tracking-tight">{r.title}</div>
                    </div>
                  )}
                  <div className="p-6 flex items-center justify-between gap-3">
                    <div>
                      <div className="font-display text-xl tracking-tight">{r.title}</div>
                      <div className="text-xs text-[#5C5A55] mt-1">{r.subtitle}</div>
                    </div>
                    {isClickable && (
                      <ArrowUpRight size={20} weight="bold" className="text-[#5C5A55] group-hover:text-[#EB0D0D] group-hover:translate-x-1 group-hover:-translate-y-1 transition-all flex-shrink-0" />
                    )}
                  </div>
                </Wrapper>
              );
            })}
          </div>
        </div>

        {/* Awards section */}
        <div className="mt-32">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
            <div>
              <Trophy size={40} weight="duotone" className="text-[#EB0D0D] mb-4" />
              <div className="overline mb-3">I premi vinti</div>
              <h2 className="font-display text-4xl md:text-5xl tracking-tighter leading-[0.95]">
                {statPremi > 0 ? `${statPremi} ${statPremi === 1 ? "riconoscimento" : "riconoscimenti"}` : "I nostri riconoscimenti"},<br />
                <em className="italic font-light text-[#EB0D0D]">categorie diverse</em>.
              </h2>
            </div>
          </div>

          {loadingAwards && (
            <div className="flex justify-center py-16">
              <CircleNotch size={28} weight="bold" className="animate-spin text-[#EB0D0D]" />
            </div>
          )}

          {!loadingAwards && awardsError && (
            <div className="bg-[#F0EFEB] border border-[#EB0D0D]/30 p-8 text-center text-[#5C5A55]">
              Errore nel caricamento dei premi. Riprova più tardi.
            </div>
          )}

          {!loadingAwards && !awardsError && awards.length === 0 && (
            <div className="bg-[#F0EFEB] border border-[#D1CFC7]/40 p-12 text-center text-[#5C5A55]">
              Elenco premi in aggiornamento.
            </div>
          )}

          {!loadingAwards && !awardsError && awards.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
              {awards.map((a, i) => (
                <article
                  key={a.id}
                  data-testid={`award-${i}`}
                  className={`group relative overflow-hidden border border-[#D1CFC7]/40 bg-[#F0EFEB] ${a.featured ? "md:col-span-1" : ""}`}
                >
                  <div className="relative aspect-[4/5] overflow-hidden bg-[#1A1918] flex items-center justify-center p-3">
                    {a.image_url ? (
                      <img
                        src={a.image_url}
                        alt={`${a.product} — Vincitore ${a.year}${a.category ? " " + a.category : ""}`}
                        className="w-full h-full object-contain transition-transform duration-700 group-hover:scale-105"
                      />
                    ) : (
                      <Trophy size={80} weight="duotone" className="text-[#EB0D0D]" />
                    )}
                  </div>
                  <div className="p-6 md:p-8">
                    <div className="flex items-baseline justify-between gap-4 mb-3">
                      <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#EB0D0D]">
                        Vincitore {a.year}
                      </div>
                      {a.brand && (
                        <div className="text-xs uppercase tracking-[0.15em] text-[#5C5A55]">
                          {a.brand}
                        </div>
                      )}
                    </div>
                    <h3 className="font-display text-2xl md:text-3xl tracking-tight mb-2">{a.product}</h3>
                    {a.category && (
                      <div className="text-sm text-[#5C5A55]">
                        Categoria: <span className="text-[#1A1918] font-medium">{a.category}</span>
                      </div>
                    )}
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>

        {/* Stats / quality */}
        <div className="mt-32 bg-[#1A1918] text-[#FAF9F6] p-10 md:p-16 lg:p-20 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7">
            <div className="text-xs uppercase tracking-[0.25em] text-[#EB0D0D] mb-4">La qualità riconosciuta</div>
            <h3 className="font-display text-3xl md:text-4xl lg:text-5xl tracking-tighter leading-[0.95]">
              Scegli i prodotti che hanno fatto la differenza al panel di esperti del settore.
            </h3>
          </div>
          <div className="md:col-span-5 grid grid-cols-3 gap-4">
            <div className="border-t-2 border-[#EB0D0D] pt-4">
              <div className="font-display text-3xl font-medium">{statPremi}</div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#FAF9F6]/60 mt-1">Premi</div>
            </div>
            <div className="border-t-2 border-[#EB0D0D] pt-4">
              <div className="font-display text-3xl font-medium">{statBrand}</div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#FAF9F6]/60 mt-1">Brand</div>
            </div>
            <div className="border-t-2 border-[#EB0D0D] pt-4">
              <div className="font-display text-3xl font-medium">{statAnni}</div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#FAF9F6]/60 mt-1">Anni</div>
            </div>
          </div>
        </div>

        <div className="mt-16 flex flex-wrap gap-4 justify-center">
          <Link to="/contatti" data-testid="premi-cta-preventivatore" className="btn-primary">
            Richiedi preventivo gratuito <ArrowUpRight size={16} weight="bold" />
          </Link>
          <Link to="/prodotti" data-testid="premi-cta-prodotti" className="btn-outline">
            Esplora i prodotti
          </Link>
        </div>
      </div>
    </div>
  );
}
